import logoDark from "@public/logo.svg";
import logoLight from "@public/logo_on_light.svg";
import { useState, useTransition } from "react";
import { useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "@/components/ui/link";
import { authClient } from "@/lib/auth-client";
import { useT } from "@/lib/i18n/use-t";
import { getPostLoginRedirect, stripLoginOnlyParams } from "@/lib/post-login-redirect";
import { T } from "./page.i18n";

export default function Verify2FAPage() {
  const t = useT(T);
  const [searchParams] = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);

  // Back to login keeps the login continuation (?redirect=, MCP OAuth params)
  // so signing in again still lands on the requested page.
  const loginQuery = stripLoginOnlyParams(searchParams).toString();
  const loginPath = loginQuery ? `/login?${loginQuery}` : "/login";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!code.trim()) {
      setError(t.enterCode);
      return;
    }

    startTransition(async () => {
      let result: { error: { message?: string } | null };

      if (useBackupCode) {
        // Verify with backup code
        result = await authClient.twoFactor.verifyBackupCode({
          code: code.trim(),
        });
      } else {
        // Verify with TOTP
        result = await authClient.twoFactor.verifyTotp({
          code: code.trim(),
          trustDevice,
        });
      }

      if (result.error) {
        setError(result.error.message || t.invalidCode);
        return;
      }

      // Success — full page reload (not navigate) so the session is re-fetched
      // from the new cookie. A client-side navigate races AuthGuard against the
      // auth client's async session refetch and can bounce back to /login.
      // The login page forwards its query here, so an MCP OAuth flow resumes at
      // the API's authorize endpoint and a ?redirect= lands on the requested page.
      window.location.href = getPostLoginRedirect(searchParams);
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-full max-w-md">
        <Card className="w-full p-8">
          <div className="mb-8 text-center">
            <div className="flex justify-center mb-4">
              {/* Show light mode logo by default, dark mode logo when .dark class is present */}
              <img
                src={logoLight}
                alt="Quantfolio Platform"
                width={280}
                height={86}
                className="dark:hidden"
              />
              <img
                src={logoDark}
                alt="Quantfolio Platform"
                width={280}
                height={86}
                className="hidden dark:block"
              />
            </div>
            <h1 className="text-2xl font-bold">{t.title}</h1>
            <p className="text-muted-foreground mt-2">
              {useBackupCode ? t.enterBackupCode : t.enterAppCode}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="code">{useBackupCode ? t.backupCode : t.verificationCode}</Label>
              <Input
                id="code"
                type="text"
                value={code}
                onChange={(e) => {
                  if (useBackupCode) {
                    setCode(e.target.value);
                  } else {
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  }
                }}
                placeholder={useBackupCode ? t.backupCodePlaceholder : t.appCodePlaceholder}
                maxLength={useBackupCode ? 20 : 6}
                autoComplete="one-time-code"
                autoFocus
                disabled={isPending}
              />
            </div>

            {!useBackupCode && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="trustDevice"
                  checked={trustDevice}
                  onCheckedChange={(checked) => setTrustDevice(checked === true)}
                  disabled={isPending}
                />
                <Label htmlFor="trustDevice" className="text-sm font-normal cursor-pointer">
                  {t.trustDevice}
                </Label>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={!useBackupCode && code.length !== 6}
              loading={isPending}
            >
              {t.verify}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setUseBackupCode(!useBackupCode);
                  setCode("");
                  setError(null);
                }}
                className="text-sm text-primary hover:underline"
              >
                {useBackupCode ? t.useApp : t.useBackupCode}
              </button>
            </div>

            <div className="text-center pt-4 border-t">
              <Link to={loginPath} className="text-sm text-muted-foreground hover:underline">
                {t.backToLogin}
              </Link>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
